/**
 * Authentication and tenant context.
 *
 * Holds the signed-in user, their memberships, and which business is currently
 * selected. The selected business id is the prefix for every scoped API call,
 * mirroring the API's `/businesses/:businessId/...` shape.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  memberPermissions,
  type AuthSession,
  type AuthUser,
  type MembershipSummary,
  type Permission,
} from '@cenox/shared';
import {
  api,
  refreshSession,
  setAccessToken,
  setUnauthenticatedHandler,
  setWorkspaceLostHandler,
} from './api';

const SELECTED_BUSINESS_KEY = 'cenox.selectedBusinessId';

interface AuthContextValue {
  user: AuthUser | null;
  memberships: MembershipSummary[];
  business: MembershipSummary | null;
  /** Effective permissions in the selected business. */
  can: (permission: Permission) => boolean;
  /** True until the initial session restore finishes. */
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthSession>;
  /** Sign in a location login: `holyghost` plus the business code from its link. */
  locationLogin: (
    businessCode: string,
    username: string,
    password: string,
  ) => Promise<AuthSession>;
  acceptInvitation: (
    token: string,
    input: { fullName?: string; password?: string },
  ) => Promise<AuthSession>;
  register: (input: {
    fullName: string;
    email: string;
    password: string;
    phone?: string;
  }) => Promise<AuthSession>;
  logout: () => Promise<void>;
  selectBusiness: (businessId: string) => void;
  /** Re-read /auth/me — call after creating a business or changing roles. */
  reload: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [memberships, setMemberships] = useState<MembershipSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    localStorage.getItem(SELECTED_BUSINESS_KEY),
  );
  const [loading, setLoading] = useState(true);

  const applySession = useCallback((session: AuthSession) => {
    setAccessToken(session.accessToken);
    setUser(session.user);
    setMemberships(session.memberships);

    setSelectedId((current) => {
      // Keep the current selection if it is still valid; otherwise fall back
      // to the first business so the app never renders a dangling tenant.
      const stillValid = current && session.memberships.some((m) => m.businessId === current);
      const next = stillValid ? current : (session.memberships[0]?.businessId ?? null);
      if (next) localStorage.setItem(SELECTED_BUSINESS_KEY, next);
      else localStorage.removeItem(SELECTED_BUSINESS_KEY);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setMemberships([]);
    setSelectedId(null);
    localStorage.removeItem(SELECTED_BUSINESS_KEY);
  }, []);

  // Restore the session on load. The refresh cookie survives a page reload;
  // the in-memory access token does not, which is the trade for keeping it out
  // of storage a script can read.
  //
  // Through `refreshSession()`, never a request of its own. StrictMode mounts
  // this effect twice in development; both runs share one rotation, and the
  // second — the one that stays mounted — applies its result. Posting to
  // /auth/refresh directly sent two refreshes with the same cookie, which the
  // server rightly reads as token theft: it revoked the session and every
  // reload ended on the sign-in page.
  useEffect(() => {
    let cancelled = false;

    void refreshSession().then((session) => {
      if (cancelled) return;
      if (session) applySession(session);
      else clear();
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [applySession, clear]);

  useEffect(() => {
    setUnauthenticatedHandler(() => clear());
    return () => setUnauthenticatedHandler(null);
  }, [clear]);

  const login = useCallback(
    async (email: string, password: string) => {
      const session = await api.post<AuthSession>('/auth/login', { email, password });
      applySession(session);
      return session;
    },
    [applySession],
  );

  /**
   * Sign in as a LOCATION.
   *
   * `holyghost` is unique within one business, not across CenoX, so the
   * business code travels with it — the sign-in link supplies both. Everything
   * after this is an ordinary session.
   */
  const locationLogin = useCallback(
    async (businessCode: string, username: string, password: string) => {
      const session = await api.post<AuthSession>('/auth/location/login', {
        businessCode,
        username,
        password,
      });
      applySession(session);
      return session;
    },
    [applySession],
  );

  const register = useCallback(
    async (input: { fullName: string; email: string; password: string; phone?: string }) => {
      const session = await api.post<AuthSession>('/auth/register', input);
      applySession(session);
      return session;
    },
    [applySession],
  );

  /**
   * Redeem an invitation and start the session it grants.
   *
   * Goes through `applySession` like login and registration do, so the
   * recipient lands inside the business rather than at a sign-in form —
   * an invitation that ends at "now go and sign in" is one half of them
   * abandon.
   */
  const acceptInvitation = useCallback(
    async (token: string, input: { fullName?: string; password?: string }) => {
      const session = await api.post<AuthSession>(`/auth/invitations/${token}/accept`, input);
      applySession(session);
      return session;
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      clear();
    }
  }, [clear]);

  const reload = useCallback(async () => {
    const me = await api.get<{ user: AuthUser; memberships: MembershipSummary[] }>('/auth/me');
    setUser(me.user);
    setMemberships(me.memberships);
    setSelectedId((current) => {
      const stillValid = current && me.memberships.some((m) => m.businessId === current);
      const next = stillValid ? current : (me.memberships[0]?.businessId ?? null);
      // Forget a selection that no longer exists, rather than keep offering it.
      if (next) localStorage.setItem(SELECTED_BUSINESS_KEY, next);
      else localStorage.removeItem(SELECTED_BUSINESS_KEY);
      return next;
    });
  }, []);

  /*
    Removed from a business, or it was deleted: re-read who this person is and
    where they belong. The selection then falls to another business they are in,
    or to none — and the Business shell offers Create / Join.
  */
  useEffect(() => {
    let busy = false;
    setWorkspaceLostHandler(() => {
      if (busy) return;
      busy = true;
      void reload()
        .catch(() => undefined)
        .finally(() => {
          busy = false;
        });
    });
    return () => setWorkspaceLostHandler(null);
  }, [reload]);

  const selectBusiness = useCallback((businessId: string) => {
    localStorage.setItem(SELECTED_BUSINESS_KEY, businessId);
    setSelectedId(businessId);
  }, []);

  const business = useMemo(
    () => memberships.find((m) => m.businessId === selectedId) ?? null,
    [memberships, selectedId],
  );

  // Mirrors the API's permission matrix so the UI hides what the server would
  // refuse. The server remains the authority — this is only to avoid showing
  // people buttons that will fail.
  const permissions = useMemo(
    () => (business ? memberPermissions(business.role, business.permissionOverrides ?? null) : new Set<Permission>()),
    [business],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      memberships,
      business,
      can: (permission) => permissions.has(permission),
      loading,
      login,
      locationLogin,
      register,
      acceptInvitation,
      logout,
      selectBusiness,
      reload,
    }),
    [user, memberships, business, permissions, loading, login, locationLogin, register, acceptInvitation, logout, selectBusiness, reload],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

/**
 * Join a business id and a path into an API URL.
 *
 * The empty path is the business itself, and it must not pick up a trailing
 * slash on the way: the API matches `/businesses/:id`, and `/businesses/:id/`
 * is a different route that does not exist. That 404 is answered by the router
 * before any handler runs, so it arrives looking like a permission or tenancy
 * problem when it is neither — which is how the Settings page stayed
 * unloadable without anyone being able to see why.
 *
 * Exported, and separate from the hook, so the joining can be tested without a
 * React tree. Every server-side test writes these URLs by hand and writes them
 * correctly; only the browser builds them this way.
 */
export function businessPath(businessId: string, path: string): string {
  const base = `/businesses/${businessId}`;
  if (path === '' || path === '/') return base;
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * The API path prefix for the selected business.
 * Throws if called outside a tenant — a bug, not a user state.
 */
export function useBusinessPath(): (path: string) => string {
  const { business } = useAuth();
  return useCallback(
    (path: string) => {
      if (!business) throw new Error('No business selected');
      return businessPath(business.businessId, path);
    },
    [business],
  );
}
